import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const LOGO = `${process.env.PUBLIC_URL}/avenor.png`;
const BACKGROUND_IMAGE = `${process.env.PUBLIC_URL}/background.png`;
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.insightos.co.uk';

export default function ServiceUnavailable() {
  const [status, setStatus] = useState('Checking database connection…');
  const [navigated, setNavigated] = useState(false);
  const navigate = useNavigate();

  // Orb configurations (same as in LoadingComponent)
  const orbConfigs = [
    {
      className: 'w-8 h-8 bg-blue-400 opacity-70',
      animate: { x: [0, 200, -100, 150, 0], y: [0, -150, 100, -50, 0], scale: [1, 1.2, 0.8, 1.5, 1] },
      transition: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
      style: { top: '20%', left: '10%' },
    },
    {
      className: 'w-6 h-6 bg-blue-500 opacity-60',
      animate: { x: [0, -180, 120, -80, 0], y: [0, 120, -80, 160, 0], scale: [1, 0.7, 1.3, 0.9, 1] },
      transition: { duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 },
      style: { top: '60%', right: '15%' },
    },
    {
      className: 'w-10 h-10 bg-blue-300 opacity-50',
      animate: { x: [0, 100, -200, 50, 0], y: [0, -100, 80, -120, 0], scale: [1, 1.1, 0.6, 1.4, 1] },
      transition: { duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 },
      style: { bottom: '20%', left: '20%' },
    },
    {
      className: 'w-7 h-7 bg-purple-400 opacity-70',
      animate: { x: [0, -150, 80, -120, 0], y: [0, 100, -140, 60, 0], scale: [1, 1.3, 0.8, 1.1, 1] },
      transition: { duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
      style: { top: '40%', right: '20%' },
    },
    {
      className: 'w-9 h-9 bg-purple-500 opacity-60',
      animate: { x: [0, 160, -90, 200, 0], y: [0, -80, 120, -160, 0], scale: [1, 0.9, 1.2, 0.7, 1] },
      transition: { duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 1.5 },
      style: { bottom: '30%', right: '10%' },
    },
    {
      className: 'w-5 h-5 bg-purple-300 opacity-80',
      animate: { x: [0, -120, 160, -80, 0], y: [0, -60, 90, -110, 0], scale: [1, 1.4, 0.6, 1.2, 1] },
      transition: { duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 3 },
      style: { top: '10%', right: '30%' },
    },
    {
      className: 'w-4 h-4 bg-blue-200 opacity-40',
      animate: { x: [0, 80, -60, 100, 0], y: [0, -40, 70, -90, 0], scale: [1, 0.8, 1.1, 0.9, 1] },
      transition: { duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2.5 },
      style: { bottom: '10%', left: '40%' },
    },
    {
      className: 'w-3 h-3 bg-purple-200 opacity-50',
      animate: { x: [0, -70, 90, -40, 0], y: [0, 50, -80, 110, 0], scale: [1, 1.2, 0.7, 1.3, 1] },
      transition: { duration: 8.5, repeat: Infinity, ease: 'easeInOut', delay: 1.8 },
      style: { top: '70%', left: '60%' },
    },
  ];

  useEffect(() => {
    let checkInterval = null;

    const checkServices = async () => {
      if (navigated) return;

      try {
        // 1. Check database connection
        const dbResponse = await axios.get(`${API_BASE}/api/db-connection`, {
          withCredentials: true,
        });

        if (dbResponse.data?.success) {
          setStatus('Database connected. Redirecting…');

          // Connectivity is back — this is the leads app, so always return to /leads.
          // ProtectedRoute will bounce to /leads/login if the session has since expired.
          navigate('/leads', { replace: true });
          setNavigated(true);
          return;
        } else {
          setStatus('Database connection failed. Retrying…');
        }
      } catch (error) {
        console.error('Service check failed', error);
        setStatus('Service check failed. Retrying…');
      }
    };

    // Initial check, then every 5 seconds
    checkServices();
    checkInterval = setInterval(checkServices, 5000);

    return () => clearInterval(checkInterval);
  }, [navigate, navigated]);

  return (
    <div
      className="min-h-screen w-full bg-slate-900 text-white relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(17, 65, 175, 0.75), rgba(51, 3, 103, 0.9)), url(${BACKGROUND_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Animated Orbs */}
      {orbConfigs.map((orb, index) => (
        <motion.div
          key={index}
          className={`absolute rounded-full ${orb.className}`}
          animate={orb.animate}
          transition={orb.transition}
          style={orb.style}
        />
      ))}

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-6 py-8">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="InsightOS" className="h-10 w-10" />
          <div className="text-base font-semibold tracking-wide text-slate-100">Avenor AI</div>
        </div>

        <div className="flex flex-1 items-center">
          <div className="rounded-3xl bg-white/10 p-8 shadow-xl backdrop-blur">
            <p className="text-2xl font-semibold text-white sm:text-3xl">
              We are currently experiencing technical difficulties.
            </p>
            <p className="mt-4 text-base text-slate-200">
              Our engineers are working on the issue and we hope to resolve it as soon as possible.
            </p>
            <p className="mt-2 text-base text-slate-200">
              Status: <span className="font-medium text-blue-300">{status}</span>
            </p>
            <p className="mt-2 text-base text-slate-200">
              For more information please contact{' '}
              <a href="mailto:support@insightos.co.uk" className="font-semibold text-teal-200 underline">
                support@insightos.co.uk
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}